import { useContext, useState } from "react";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import type { UnpackResponse } from "../../../../api/src/lib";
import type { PortalController } from "../../../../api/src/portal/portal.controller";
import {
  deserializeDate,
  useAuthenticatedMutationNew,
  useAuthenticatedQuery,
} from "../../lib/api";
import { HouseholdContext } from "./HouseholdInfo";

type BindingsResponse = UnpackResponse<PortalController["listBindings"]>;
type BindingStatus = BindingsResponse["data"][number];

/** The portal status of each eligible contact in the household (invite spec IL-30). */
function useHouseholdPortalBindings(householdId: number) {
  return useAuthenticatedQuery<
    BindingsResponse,
    BindingStatus[],
    [string, string, number, string]
  >({
    queryKey: ["portal", "households", householdId, "bindings"],
    queryFn: async (response) =>
      (response?.data ?? []).map((binding) => ({
        ...binding,
        inviteExpiresAt: deserializeDate(binding.inviteExpiresAt),
      })),
  });
}

/** Send — author + issue for an operator-selected contact (invite spec IL-31). */
function useSendInvite(householdId: number) {
  const queryClient = useQueryClient();
  return useAuthenticatedMutationNew<
    UnpackResponse<PortalController["send"]>,
    string,
    { contactId: number }
  >({
    mutationKey: ["POST", "portal", "invitations"],
    mutationFn: (response) => {
      queryClient.invalidateQueries({
        queryKey: ["portal", "households", householdId, "bindings"],
      });
      return response.data.inviteUrl;
    },
  });
}

/** Resend — re-arm a live pending/superseded binding (invite spec IL-10). */
function useResendInvite(householdId: number, portalUserId: number) {
  const queryClient = useQueryClient();
  return useAuthenticatedMutationNew<
    UnpackResponse<PortalController["resend"]>,
    string,
    void
  >({
    mutationKey: ["POST", "portal", "invitations", portalUserId, "resend"],
    mutationFn: (response) => {
      queryClient.invalidateQueries({
        queryKey: ["portal", "households", householdId, "bindings"],
      });
      return response.data.inviteUrl;
    },
  });
}

/** Revoke — DB-authoritative flip to revoked (invite spec IL-11). */
function useRevokeInvite(householdId: number, portalUserId: number) {
  const queryClient = useQueryClient();
  return useAuthenticatedMutationNew<
    UnpackResponse<PortalController["revoke"]>,
    void,
    void
  >({
    mutationKey: ["POST", "portal", "invitations", portalUserId, "revoke"],
    mutationFn: () => {
      queryClient.invalidateQueries({
        queryKey: ["portal", "households", householdId, "bindings"],
      });
    },
  });
}

/** The advisor-facing label for a binding state (superseded reads as in-flight). */
function statusBadge(state: BindingStatus["inviteState"]) {
  switch (state) {
    case "active":
      return <Badge bg="success">Active</Badge>;
    case "pending":
    case "superseded":
      return <Badge bg="info">Invited</Badge>;
    case "revoked":
      return <Badge bg="secondary">Revoked</Badge>;
    default:
      return (
        <Badge bg="light" text="dark">
          Not invited
        </Badge>
      );
  }
}

/**
 * The client portal section of the advisor management page (invite spec
 * IL-30/IL-31): one row per eligible person contact showing its portal status,
 * with send / resend / revoke controls driving the advisor-app write path.
 */
const HouseholdPortal = () => {
  const { household } = useContext(HouseholdContext);
  const householdId = household?.id ?? -1;
  const { data, isPending, isError } = useHouseholdPortalBindings(householdId);

  // On dev no invitation email sends, so surface the accept link for testing.
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  if (isPending) {
    return <Spinner animation="border" role="status" className="m-3" />;
  }
  if (isError) {
    return (
      <Alert variant="danger" className="m-3">
        Could not load the portal status for this household.
      </Alert>
    );
  }

  const contacts = data ?? [];

  return (
    <div className="p-3">
      {inviteUrl !== null && (
        <Alert variant="info" dismissible onClose={() => setInviteUrl(null)}>
          Invitation link (Auth0 emails this to the client; shown here for
          testing): <a href={inviteUrl}>{inviteUrl}</a>
        </Alert>
      )}

      {contacts.length === 0 ? (
        <p className="text-muted">
          No eligible contacts. Only a household member with an email address
          can be invited to the client portal.
        </p>
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Portal status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => (
              <PortalContactRow
                key={contact.contactId}
                contact={contact}
                householdId={householdId}
                onInviteUrl={setInviteUrl}
              />
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

function PortalContactRow({
  contact,
  householdId,
  onInviteUrl,
}: {
  contact: BindingStatus;
  householdId: number;
  onInviteUrl: (url: string) => void;
}) {
  const portalUserId = contact.portalUserId ?? -1;
  const send = useSendInvite(householdId);
  const resend = useResendInvite(householdId, portalUserId);
  const revoke = useRevokeInvite(householdId, portalUserId);

  const busy = send.isPending || resend.isPending || revoke.isPending;
  const state = contact.inviteState;
  const canSend = state === "none" || state === "revoked";
  const canManage = state === "pending" || state === "superseded";
  const canRevoke = canManage || state === "active";
  const hasEmail = (contact.email ?? "").length > 0;

  const expiresAt = contact.inviteExpiresAt;

  return (
    <tr>
      <td>{contact.name}</td>
      <td>{contact.email ?? <span className="text-muted">—</span>}</td>
      <td>
        {statusBadge(state)}
        {expiresAt !== null &&
          (state === "pending" || state === "superseded") && (
            <div className="small text-muted">
              Expires {expiresAt.toLocaleDateString()}
            </div>
          )}
      </td>
      <td className="text-end">
        {canSend && (
          <Button
            size="sm"
            variant="primary"
            disabled={busy || !hasEmail}
            title={hasEmail ? undefined : "This contact has no email address."}
            onClick={() =>
              send
                .mutateAsync({ contactId: contact.contactId })
                .then((url) => onInviteUrl(url))
            }
          >
            Send invite
          </Button>
        )}
        {canManage && (
          <Button
            size="sm"
            variant="primary"
            className="me-2"
            disabled={busy}
            onClick={() => resend.mutateAsync().then((url) => onInviteUrl(url))}
          >
            Resend
          </Button>
        )}
        {canRevoke && (
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={() => revoke.mutate()}
          >
            Revoke
          </Button>
        )}
      </td>
    </tr>
  );
}

export default HouseholdPortal;
